import { useFindingOnAsset } from "~/pages/findings/FindingOnAsset/hooks";
import { PolicyMrnToURIEncodedId } from "~/lib/mrn";
import { isCheckFinding } from "~/pages/findings/FindingOnAsset/utils";
import { useNavigate } from "react-router-dom";
import { SectionHeading } from "~/components/DetailsPage/components";
import { Box, Typography } from "@mui/material";
import {
  DataGrid,
  gridClasses,
  GridColDef,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import { ComplianceCell, TextCell } from "~/components/DataGridCells";
import { StatusCell } from "~/components/Cells/PreviewStatusCell";
import { policyIcon } from "~/pages/inventory/utils/policyIcon";
import { ActivePolicy } from "~/operations";

function PolicyNameCell(params: GridRenderCellParams) {
  const { name } = params.row;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        height: "100%",
      }}
    >
      {policyIcon(name, "medium")}
      <Typography
        variant="body2"
        sx={{ lineHeight: "157%", letterSpacing: ".1px" }}
      >
        {name}
      </Typography>
    </Box>
  );
}

function getColumns(): GridColDef<ActivePolicy>[] {
  return [
    {
      field: "name",
      headerName: "Policy",
      headerClassName: "left-cell",
      cellClassName: "left-cell",
      minWidth: 250,
      flex: 1,
      resizable: false,
      renderCell: PolicyNameCell,
    },
    {
      field: "action",
      headerName: "Status",
      width: 120,
      resizable: false,
      renderCell: StatusCell,
    },
    {
      field: "assets",
      headerName: "Assets",
      width: 100,
      resizable: false,
      align: "left",
      headerAlign: "left",
      valueGetter: (_value, row) => row.score?.blastRadius?.assets || 0,
      renderCell: TextCell,
    },
    {
      field: "compliance",
      headerName: "Policy Compliance",
      headerClassName: "right-cell",
      cellClassName: "right-cell",
      width: 200,
      resizable: false,
      valueGetter: (_value, row) => {
        const assets = row.score?.blastRadius?.assets || 0;
        const affected = row.score?.blastRadius?.affected || 0;
        if (!assets) return 100;
        return ((assets - affected) / assets) * 100;
      },
      renderCell: ComplianceCell,
    },
  ];
}

const tableHeaderStyles = {
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "normal",
  color: "text.primary",
};

export const RelatedPolicies = () => {
  const { finding, activeScope } = useFindingOnAsset();
  const navigate = useNavigate();

  if (!isCheckFinding(finding) || !activeScope) return;

  const rows = (finding?.relatedPolicies || [])
    .filter((policy): policy is ActivePolicy => Boolean(policy))
    .map((policy) => ({
      id: policy.mrn,
      ...policy,
    }));

  const columns = getColumns();

  const handleRowClick = (params: { row: ActivePolicy }) => {
    const policyHref = `/space/findings/policies/${PolicyMrnToURIEncodedId(
      params.row.mrn,
    )}/checks?${activeScope.params}`;
    navigate(policyHref);
  };

  return (
    <>
      <SectionHeading heading="Related policies" />
      <Box sx={{ width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          columnHeaderHeight={58}
          rowHeight={54}
          disableRowSelectionOnClick
          disableColumnMenu
          hideFooter
          onRowClick={handleRowClick}
          sx={{
            borderRadius: 2,
            border: "none",
            backgroundColor: "background.paper",
            [`& .${gridClasses.columnSeparator}`]: {
              [`&:not(.${gridClasses["columnSeparator--resizable"]})`]: {
                display: "none",
              },
            },
            [`& .${gridClasses.columnHeader}`]: {
              ...tableHeaderStyles,
              backgroundColor: (theme) =>
                theme.palette.mode === "light" ? "#D9D9DA" : "background.dark",
              borderBottom: "none !important",
            },
            [`& .${gridClasses.sortIcon}`]: {
              fontSize: "10px",
              color: "text.primary",
            },
            // remove focus outline
            [`& .${gridClasses.columnHeader}:focus`]: {
              outline: "none",
            },
            [`& .${gridClasses.columnHeader}:focus-within`]: {
              outline: "none",
            },
            [`& .${gridClasses.cell}:focus`]: {
              outline: "none",
            },
            [`& .${gridClasses.cell}:focus-within`]: {
              outline: "none",
            },
            "& .MuiDataGrid-row--borderBottom": {
              borderBottom: "none",
            },
            "& .left-cell": {
              pl: 3,
            },
            "& .right-cell": {
              pr: 3,
            },
            "& .MuiDataGrid-row": {
              cursor: "pointer",
            },
          }}
        />
      </Box>
    </>
  );
};
